import { Core } from "@faire/design-tokens";
import { trackRetailerPosPdpInventoryCountView } from "@faire/web-api/events/retailerPos/view/pdpInventoryCount";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { IGetProductOptionInventoryRequest } from "@faire/web-api/indigofair/retailers/pos/IGetProductOptionInventoryRequest";
import { useQuery_escape_hatch as useInventoryQuery } from "@faire/web-api/retailers-pos-api/product-option-inventory/post-hooks";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import styled from "styled-components";

import { PerStyleVariationMapBuilder } from "@faire/retailer/features/OpenSizing/PerStyleVariationMapBuilder";
import { OpenSizingInventoryHeader } from "@faire/retailer/features/PointOfSale/components/PDPInventory/OpenSizingInventoryHeader";
import { OpenSizingVariantPOSInventory } from "@faire/retailer/features/PointOfSale/components/PDPInventory/OpenSizingVariantPOSInventory";
import { usePOSSummary } from "@faire/retailer/features/PointOfSale/hooks/usePOSSummary";
import {
  useAssignSettingPDPInventory,
  useSettingPDPInventory,
} from "@faire/retailer/settings/useSettingPDPInventory";
import { QuantityPicker } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/QuantityPicker";
import { SizeChartLink } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/SizeChartLink";

import { OpenSizingVariationHelper } from "./OpenSizingVariationHelper";
import { OpenSizingVariationInput } from "./OpenSizingVariationInput";
import { OpenSizingVariationLabel } from "./OpenSizingVariationLabel";

interface IProps {
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  perStyleOptionVariation: PerStyleVariationMapBuilder.IPerStyleOptionVariation[];
  minimumOrderQuantity: number;
  perStyleTotalQuantity: number;
  updateSelectedQuantityByToken: (token: string, quantity: number) => void;
}

const OpenSizingVariantTableBase: React.FC<IProps> = ({
  brand,
  product,
  perStyleOptionVariation,
  minimumOrderQuantity,
  perStyleTotalQuantity,
  updateSelectedQuantityByToken,
}) => {
  const { hasLinkedPOS } = usePOSSummary();
  const showInventory = useSettingPDPInventory();
  const assignPDPInventory = useAssignSettingPDPInventory();
  const { data } = useInventoryQuery(
    IGetProductOptionInventoryRequest.build({
      product_option_tokens: perStyleOptionVariation.map(
        (variation) => variation.productOptionToken
      ),
    }),
    { enabled: hasLinkedPOS }
  );

  const hasOptionsWithInventory = useMemo(
    () =>
      perStyleOptionVariation.some(
        (variation) =>
          data?.inventory_data_by_product_option[variation.productOptionToken]
            ?.total_inventory !== undefined
      ),
    [data, perStyleOptionVariation]
  );

  const getPOSInventory = (token: string) =>
    data?.inventory_data_by_product_option[token]?.total_inventory;

  const handleChange = (token: string) => (value: number) =>
    updateSelectedQuantityByToken(token, Math.max(value, 0));

  useEffect(() => {
    if (hasOptionsWithInventory) {
      assignPDPInventory();
    }
  }, [hasOptionsWithInventory, assignPDPInventory]);

  if (showInventory) {
    return (
      <Column style={{ width: "100%" }}>
        <Grid style={{ alignItems: "end" }}>
          <Row gap="1x" style={{ flexWrap: "wrap" }}>
            <Typography>
              <StrictLocalMsg
                defaultMessage="Sizes"
                description={{
                  text: "You can select sizes for this product variant here",
                }}
              />
            </Typography>
            <SizeChartLink brand={brand} product={product} />
          </Row>
          {hasLinkedPOS ? (
            <OpenSizingInventoryHeader isEmpty={!hasOptionsWithInventory} />
          ) : (
            <Spacer size="1x" />
          )}
          <Typography
            style={{ justifySelf: "center" }}
            data-test-id="open-sizing-table-moq"
          >
            <StrictLocalMsg
              defaultMessage="Min Qty {minimumOrderQuantity}"
              values={DEPRECATED_backwardsCompatibleValues({
                minimumOrderQuantity,
              })}
              description={{
                text: "The minimum quantity of {minimumOrderQuantity} must be selected to place an order",
              }}
            />
          </Typography>
        </Grid>
        <Spacer height="1x" />
        <HorizontalLine />
        {perStyleOptionVariation.map((variation) => (
          <TableRow
            key={variation.productOptionToken}
            variation={variation}
            onChange={handleChange(variation.productOptionToken)}
            posInventoryCount={getPOSInventory(variation.productOptionToken)}
          />
        ))}
        <Spacer width="1x" />
        <Grid>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Total"
              description={{ text: "Total quantity selected" }}
            />
          </Typography>
          <Spacer size="1x" />
          <Typography
            style={{ justifySelf: "center" }}
            data-test-id="open-sizing-total-quantity"
          >
            {perStyleTotalQuantity}
          </Typography>
        </Grid>
        <Spacer height="3x" />
      </Column>
    );
  }

  return (
    <Table>
      <Row justify="space-between">
        <Column>
          <Row>
            <Typography>
              <StrictLocalMsg
                defaultMessage="Sizes"
                description={{
                  text: "You can select sizes for this product variant here",
                }}
              />
            </Typography>
            <Spacer width="1x" />
            <SizeChartLink brand={brand} product={product} />
          </Row>
        </Column>
        <Column>
          <Typography data-test-id="open-sizing-table-moq">
            <StrictLocalMsg
              defaultMessage="Min Qty {minimumOrderQuantity}"
              values={DEPRECATED_backwardsCompatibleValues({
                minimumOrderQuantity,
              })}
              description={{
                text: "The minimum quantity of {minimumOrderQuantity} must be selected to place an order",
              }}
            />
          </Typography>
        </Column>
      </Row>
      <Spacer height={6} />
      {perStyleOptionVariation.map((variation, i) => (
        <QuantityPicker
          key={variation.size}
          variation={variation}
          onChange={handleChange(variation.productOptionToken)}
          isFirst={i === 0}
        />
      ))}
      <TotalRow>
        <Row align="center">
          <Spacer width="2x" />
          <Typography>
            <StrictLocalMsg
              defaultMessage="Total"
              description={{ text: "Total quantity selected" }}
            />
          </Typography>
        </Row>
        <Row align="center">
          <QuantityContainer data-test-id="open-sizing-total-quantity">
            <Typography>{perStyleTotalQuantity}</Typography>
          </QuantityContainer>
          <Spacer width="2x" />
        </Row>
      </TotalRow>
      <Spacer size="3x" />
    </Table>
  );
};

export const OpenSizingVariantTable = observer(OpenSizingVariantTableBase);

const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TotalRow = styled(Row).attrs({
  justify: "space-between",
  align: "center",
})`
  width: 100%;
  height: 50px;
  margin-top: -1px;
  border: 1px solid ${Core.border.muted};
`;

const QuantityContainer = styled(Row).attrs({
  justify: "center",
})`
  /* A little out of protocol, but it is to match
    the same width as the quantity pickers
    to align properly.
   */
  width: 120px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 120px;
  column-gap: ${getSpacing("1x")};
  align-items: center;
`;

const TableRow: React.FC<{
  variation: PerStyleVariationMapBuilder.IPerStyleOptionVariation;
  posInventoryCount: number | undefined;
  onChange: (value: number) => void;
}> = ({ variation, posInventoryCount, onChange }) => {
  const { hasLinkedPOS } = usePOSSummary();
  // TODO(Fabio): remove this event and forwarded refs when removing PDP_INVENTORY_COUNTS_240807
  const inventoryViewRef = useDoActionWhenVisible(() =>
    trackRetailerPosPdpInventoryCountView(variation.productOptionToken)
  );

  return (
    <Column
      gap="1x"
      style={{
        padding: getSpacing("1x", 0),
        borderBottom: `1px solid ${Core.border.muted}`,
      }}
    >
      <Grid>
        <OpenSizingVariationLabel variation={variation} />
        {hasLinkedPOS && posInventoryCount !== undefined ? (
          <OpenSizingVariantPOSInventory
            count={posInventoryCount}
            ref={inventoryViewRef}
          />
        ) : (
          <Spacer width="1x" />
        )}
        <OpenSizingVariationInput variation={variation} onChange={onChange} />
      </Grid>
      <OpenSizingVariationHelper variation={variation} />
    </Column>
  );
};
